import type { StockAttribution } from '@/components/StockPhotoCredit';

/**
 * Provenance stamped on an image when it was sourced, stored alongside the URL
 * in a section's `content.image_meta`. Written by the backend art-direction
 * pipeline (functions/src/services/images/art-direction.ts).
 */
export interface ImageMeta {
  /** "stock:{source}" | "ai" | "gradient" | "property" */
  source?: string;
  photographer?: string | null;
  photographer_url?: string | null;
  page_url?: string | null;
  query?: string;
  picked_at?: string;
  verified?: boolean;
}

/**
 * The credit to render for an image, or null when none is owed.
 *
 * Only stock photos carry a credit — AI renders, brand gradients, the
 * property's own photos and hand-uploaded images owe nobody. Unsplash's API
 * terms REQUIRE the credit wherever their photo appears, which is why the
 * wizard may use them at all; Pexels and Pixabay merely appreciate it, and we
 * render it for them too rather than special-casing.
 *
 * Mirrors the gate the /stay route applies server-side (functions/src/routes/
 * stay.ts) — a stock source AND a known photographer, since a credit reading
 * "Photo by null" is worse than none.
 */
export function toStockAttribution(meta: ImageMeta | null | undefined): StockAttribution | null {
  const source = meta?.source;
  if (!source?.startsWith('stock:') || !meta?.photographer) return null;
  return {
    source: source.slice('stock:'.length),
    photographer: meta.photographer,
    photographer_url: meta.photographer_url ?? null,
    page_url: meta.page_url ?? null,
  };
}
